import React from "react";
import { useQuery } from "@apollo/client";
import { DateTime } from "luxon";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import LinearProgress from "@material-ui/core/LinearProgress";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import { downloadCsv } from "../../../lib/downloadCsv";
import { buildCsv } from "./buildCsv";
import { REPORT_ROW_LIMIT } from "./constants";
import { describeEvent } from "./describeEvent";
import { pedestrianAccessEventsQuery } from "./queries";

export const PEDESTRIAN_ACCESS_COLUMNS = [
  { key: "time", label: "Time" },
  { key: "door", label: "Door" },
  { key: "credentialType", label: "Credential" },
  { key: "credentialId", label: "Credential id" },
  { key: "controller", label: "Door controller" },
  { key: "drivoCheck", label: "Drivo check" },
  { key: "note", label: "Note" },
  { key: "eventId", label: "Controller event id" },
];

const useStyles = makeStyles({
  root: { marginTop: 16, padding: 16 },
  toolbar: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  download: { marginLeft: "auto", textTransform: "none" },
  tableWrap: { overflowX: "auto", border: "1px solid var(--color-border-tertiary, #E1E4EA)", borderRadius: 8 },
  header: { fontWeight: 600, color: "var(--color-text-secondary)", whiteSpace: "nowrap" },
  cell: { whiteSpace: "nowrap" },
  empty: { padding: 28, textAlign: "center", color: "var(--color-text-secondary)" },
  limit: { marginBottom: 12, color: "#B54708" },
});

export default function PedestrianAccessList({ from, to, zone, brandingKey }) {
  const classes = useStyles();
  const { data, loading, error } = useQuery(pedestrianAccessEventsQuery, {
    variables: { from, to },
    fetchPolicy: "network-only",
  });
  const events = data?.pedestrianAccessEvents || [];
  const rows = events.map((event) => describeEvent(event, zone));
  const accepted = events.filter((event) => event.controllerGranted).length;
  const firstDay = DateTime.fromISO(from, { zone }).toISODate();
  const lastDay = DateTime.fromISO(to, { zone }).minus({ days: 1 }).toISODate();

  const handleDownload = () => downloadCsv(
    buildCsv(PEDESTRIAN_ACCESS_COLUMNS, rows),
    `pedestrian_access_${firstDay}_${lastDay}_${brandingKey || "hotel"}.csv`,
  );

  return (
    <Paper className={classes.root}>
      <Box className={classes.toolbar}>
        <Typography variant="body2">
          {rows.length} PIN entries: {accepted} accepted, {rows.length - accepted} refused by the door controller
        </Typography>
        <Button className={classes.download} variant="outlined" disabled={!rows.length} onClick={handleDownload}>
          Download CSV
        </Button>
      </Box>
      {events.length >= REPORT_ROW_LIMIT && (
        <Typography variant="body2" className={classes.limit}>
          Only the newest {REPORT_ROW_LIMIT} entries are shown and downloaded. Choose a shorter range to see the rest.
        </Typography>
      )}
      {loading && <LinearProgress aria-label="Loading pedestrian access" />}
      {error ? (
        <Box className={classes.empty}>Could not load pedestrian access. Please try again or contact support.</Box>
      ) : (
        <div className={classes.tableWrap}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {PEDESTRIAN_ACCESS_COLUMNS.map((column) => (
                  <TableCell key={column.key} className={classes.header}>{column.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  {PEDESTRIAN_ACCESS_COLUMNS.map((column) => (
                    <TableCell key={column.key} className={classes.cell}>{row[column.key]}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!loading && !rows.length && <Box className={classes.empty}>No pedestrian PIN entries in this range.</Box>}
        </div>
      )}
    </Paper>
  );
}
